import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit2, FiTrash2, FiUser } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { ConfirmModal } from "../../components/financeDetail";
import { appImages } from "../../assets/appicons";
import {
  getCustomerListApi,
  getCustomerStatisticApi,
  getCustomerTypeListApi,
  deleteCustomerApi,
} from "../../api/finance.customer.api";
import { extractErrorMessage } from "../../api/client";
import { buildCustomerAddress } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;
const ALL_TYPE = "ALL";

const StatCard = ({ title, value, icon, active, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    flex="1"
    direction="column"
    align="flex-start"
    gap="6px"
    p="12px"
    borderRadius="12px"
    border="1px solid"
    borderColor={active ? APP.primary200 : BORDER}
    bg={active ? "#EEF5FF" : "white"}
  >
    <AppIcon src={icon} size={24} color={APP.primary200} />
    <Text fontSize="12px" color={MUTED}>{title}</Text>
    <Text fontSize="20px" fontWeight={800} color={active ? APP.primary200 : TEXT}>{value}</Text>
  </Flex>
);

const CustomerCard = ({ item, onClick, onEdit, onDelete }) => {
  const rows = item.isBusiness
    ? [
        { label: "Mã số thuế", value: item.businessTaxCode || "-" },
        { label: "Số điện thoại", value: item.phone || "-" },
        { label: "Email", value: item.email || "-" },
        { label: "Địa chỉ", value: item.businessAddress || item.businessLocationAddress || "-" },
      ]
    : [
        { label: "Ngày sinh", value: item.dateOfBirth || "-" },
        { label: Number(item.identityNumber) === 2 ? "Số hộ chiếu" : "Số CCCD/CMND", value: item.identificationNumber || "-" },
        { label: "Số điện thoại", value: item.phone || "-" },
        { label: "Email", value: item.email || "-" },
        { label: "Địa chỉ", value: buildCustomerAddress(item) || "-" },
      ];
  return (
    <Box w="100%" bg="white" border="1px solid" borderColor={BORDER} borderRadius="8px" p="14px">
      <Flex align="flex-start" gap="8px">
        <Box as="button" type="button" onClick={onClick} textAlign="left" flex="1" minW="0">
          <Text fontSize="16px" fontWeight={600} color={TEXT}>{item.name || "-"}</Text>
          <Flex gap="8px" mt="8px" flexWrap="wrap">
            <Box bg={item.customerType?.color || "#E9FFF4"} borderRadius="8px" px="12px" py="4px">
              <Text fontSize="11px" fontWeight={600} color="#0BA859">{item.customerType?.name?.toUpperCase() || "KHÁCH HÀNG"}</Text>
            </Box>
            <Box bg={item.isBusiness ? "#EEF2FF" : "#FFF7ED"} borderRadius="8px" px="12px" py="4px">
              <Text fontSize="11px" fontWeight={600} color={item.isBusiness ? "#4338CA" : "#C2410C"}>{item.isBusiness ? "DOANH NGHIỆP" : "CÁ NHÂN"}</Text>
            </Box>
          </Flex>
        </Box>
        <Menu placement="bottom-end" autoSelect={false}>
          <MenuButton as={Flex} w="40px" h="40px" borderRadius="6px" bg="#EFF6FF" align="center" justify="center" flexShrink={0} cursor="pointer" _hover={{ bg: "#E1EEFF" }}>
            <Flex w="100%" h="100%" align="center" justify="center"><FiUser color={APP.primary200} size={20} /></Flex>
          </MenuButton>
          <MenuList minW="160px" py="4px">
            <MenuItem icon={<FiEdit2 size={16} />} onClick={() => onEdit(item)}>Cập nhật</MenuItem>
            <MenuItem icon={<FiTrash2 size={16} />} color="#E5484D" onClick={() => onDelete(item)}>Xóa</MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      <Box as="button" type="button" onClick={onClick} textAlign="left" w="100%">
        <Box h="1px" bg={BORDER} my="12px" />
        {rows.map((r) => (
          <Flex key={r.label} gap="12px" align="flex-start" mb="6px">
            <Text fontSize="14px" color={MUTED} flex="0 0 42%">{r.label}</Text>
            <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" noOfLines={2}>{r.value}</Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

const CustomerList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [search, setSearch] = useState("");
  const [statFilter, setStatFilter] = useState("ALL"); // ALL | PERSONAL | BUSINESS
  const [typeId, setTypeId] = useState(ALL_TYPE);

  const [stat, setStat] = useState({ total: 0, totalPersonal: 0, totalBusiness: 0 });
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const sheet = useDisclosure();
  const [toDelete, setToDelete] = useState(null);
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const goEdit = (item) =>
    navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/tao/${item.isBusiness ? "doanh-nghiep" : "ca-nhan"}?type=edit&id=${item._id}`);

  const doDelete = async () => {
    const item = toDelete;
    setToDelete(null);
    try {
      await deleteCustomerApi(item._id);
      toast.success("Xóa khách hàng thành công");
      setItems((prev) => prev.filter((c) => c._id !== item._id));
      getCustomerStatisticApi().then((d) => setStat({
        total: d?.total ?? 0,
        totalPersonal: d?.totalPersonal ?? 0,
        totalBusiness: d?.totalBusiness ?? 0,
      })).catch(() => {});
    } catch (e) {
      toast.error(extractErrorMessage(e, "Xóa khách hàng thất bại"));
    }
  };

  useEffect(() => {
    getCustomerStatisticApi().then((d) => setStat({
      total: d?.total ?? 0,
      totalPersonal: d?.totalPersonal ?? 0,
      totalBusiness: d?.totalBusiness ?? 0,
    })).catch(() => {});
    getCustomerTypeListApi().then((d) => setTypes(d?.items || d || [])).catch(() => {});
  }, []);

  const buildParams = useCallback(
    (p) => {
      const params = { page: p, limit: LIMIT, search: search || undefined };
      if (statFilter === "PERSONAL") params.isBusiness = false;
      if (statFilter === "BUSINESS") params.isBusiness = true;
      if (typeId !== ALL_TYPE) params.customerTypeId = typeId;
      return params;
    },
    [search, statFilter, typeId]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getCustomerListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statFilter, typeId]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };

  const typeOptions = useMemo(() => [{ _id: ALL_TYPE, name: "Tất cả" }, ...types], [types]);
  const typeLabel = typeOptions.find((t) => t._id === typeId)?.name || "Tất cả";

  const modals = (
    <>
      <ConfirmModal
        isOpen={!!toDelete}
        title="Xóa khách hàng"
        message={`Bạn có chắc chắn muốn xóa khách hàng ${toDelete?.name || ""}?`}
        confirmText="Xóa"
        onConfirm={doDelete}
        onClose={() => setToDelete(null)}
      />

      {/* Sheet loại khách hàng */}
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">Loại khách hàng</Text>
            <Box maxH="50vh" overflowY="auto">
              {typeOptions.map((t, i) => {
                const active = t._id === typeId;
                const last = i === typeOptions.length - 1;
                return (
                  <Flex key={t._id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={last ? "none" : "1px solid #EEF1F6"} onClick={() => { setTypeId(t._id); sheet.onClose(); }}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400}>{t.name}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => ({
      id: it._id,
      raw: it,
      name: it.name || "-",
      type: it.customerType?.name || "Khách hàng",
      kind: it.isBusiness ? "Doanh nghiệp" : "Cá nhân",
      isBusiness: !!it.isBusiness,
      identity: it.isBusiness
        ? it.businessTaxCode || "-"
        : it.identificationNumber || "-",
      phone: it.phone || "-",
      email: it.email || "-",
      address: it.isBusiness
        ? it.businessAddress || it.businessLocationAddress || "-"
        : buildCustomerAddress(it) || "-",
    }));

    return (
      <DesktopPage
        title="Khách hàng"
        right={
          <DesktopButton
            onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/tao`)}
          >
            + Tạo khách hàng
          </DesktopButton>
        }
      >
        <DesktopStatRow
          items={[
            { key: "all", title: "Tổng số", total: stat.total },
            { key: "personal", title: "Cá nhân", total: stat.totalPersonal },
            { key: "business", title: "Doanh nghiệp", total: stat.totalBusiness },
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="320px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Tìm kiếm
              </Text>
              <DesktopSearch
                placeholder="Nhập tên khách hàng ..."
                onChange={onSearchChange}
                maxW="320px"
              />
            </Box>
            <Box w="220px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Phân loại
              </Text>
              <Flex gap="8px">
                {[
                  { key: "ALL", label: "Tất cả" },
                  { key: "PERSONAL", label: "Cá nhân" },
                  { key: "BUSINESS", label: "Doanh nghiệp" },
                ].map((o) => (
                  <Box
                    key={o.key}
                    as="button"
                    px="10px"
                    h="40px"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={statFilter === o.key ? APP.primary200 : BORDER}
                    bg={statFilter === o.key ? "#EEF5FF" : "white"}
                    color={statFilter === o.key ? APP.primary200 : TEXT}
                    fontSize="13px"
                    fontWeight={600}
                    onClick={() => setStatFilter(o.key)}
                  >
                    {o.label}
                  </Box>
                ))}
              </Flex>
            </Box>
            <Box w="220px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Loại khách hàng
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={sheet.onOpen}
              >
                {typeLabel}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có khách hàng"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "name", label: "Khách hàng", align: "left", width: "18%" },
              { key: "type", label: "Loại", width: "12%" },
              {
                key: "kind",
                label: "Hình thức",
                width: "10%",
                render: (row) => (
                  <Box
                    display="inline-block"
                    bg={row.isBusiness ? "#EEF2FF" : "#FFF7ED"}
                    borderRadius="8px"
                    px="10px"
                    py="3px"
                  >
                    <Text
                      fontSize="12px"
                      fontWeight={600}
                      color={row.isBusiness ? "#4338CA" : "#C2410C"}
                    >
                      {row.kind}
                    </Text>
                  </Box>
                ),
              },
              { key: "identity", label: "CCCD / MST", width: "13%" },
              { key: "phone", label: "Số điện thoại", width: "12%" },
              { key: "email", label: "Email", align: "left", width: "15%" },
              { key: "address", label: "Địa chỉ", align: "left", width: "14%" },
              {
                key: "actions",
                label: "Thao tác",
                width: "6%",
                render: (row) => (
                  <Flex gap="8px" justify="center" onClick={(e) => e.stopPropagation()}>
                    <Flex
                      as="button"
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      bg="#EFF6FF"
                      align="center"
                      justify="center"
                      onClick={() => goEdit(row.raw)}
                      title="Cập nhật"
                    >
                      <FiEdit2 size={16} color={APP.primary200} />
                    </Flex>
                    <Flex
                      as="button"
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      bg="#FFF0F0"
                      align="center"
                      justify="center"
                      onClick={() => setToDelete(row.raw)}
                      title="Xóa"
                    >
                      <FiTrash2 size={16} color="#E5484D" />
                    </Flex>
                  </Flex>
                ),
              },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {modals}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="90px">
      <AppBar title="Khách hàng" onBack={() => navigate(EMP_BASE_PATH)} />

      <Box px="12px" pt="8px">
        {/* Search */}
        <Flex h="50px" align="center" px="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="10px">
          <AppIcon src={appImages.icon_search} size={20} color={MUTED} />
          <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Nhập tên khách hàng ..." onChange={onSearchChange} />
        </Flex>

        {/* Statistic */}
        <Flex gap="12px" mt="14px">
          <StatCard title="Tổng số" value={stat.total} icon={appImages.icon_total_customer} active={statFilter === "ALL"} onClick={() => setStatFilter("ALL")} />
          <StatCard title="Cá nhân" value={stat.totalPersonal} icon={appImages.icon_customer_personal} active={statFilter === "PERSONAL"} onClick={() => setStatFilter("PERSONAL")} />
          <StatCard title="Doanh nghiệp" value={stat.totalBusiness} icon={appImages.icon_customer_business} active={statFilter === "BUSINESS"} onClick={() => setStatFilter("BUSINESS")} />
        </Flex>

        {/* Header row + filter */}
        <Flex mt="18px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách khách hàng</Text>
          <Flex as="button" align="center" gap="6px" onClick={sheet.onOpen}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{typeLabel}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có khách hàng</Text>
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it) => (
              <CustomerCard key={it._id} item={it} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${it._id}`)} onEdit={goEdit} onDelete={setToDelete} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {/* FAB tạo khách hàng mới */}
      <Box as="button" display={{ base: "block", md: "none" }} position="fixed" right={{ base: "20px", md: "calc(50% - 220px)" }} bottom="24px" zIndex={15} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/tao`)}>
        <img src={appImages.icon_create} alt="Tạo mới" width={64} height={64} style={{ display: "block" }} />
      </Box>

      {modals}
    </Box>
  );
};

export default CustomerList;
